import { useCallback, useState } from 'react';
import { useToast } from 'hooks/useToast';
import { useAppDispatch } from 'store/hooks/useStore';
import { listActions } from 'store/reducers/menuCategoryReducer/listSlice';
import * as menuCategoryApi from 'api/menuCategoryApi';
import { MenuCategory } from 'types/menuCategory';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core';
import { OriginalMenu } from 'types/menu';
import { useListStore } from './useListStore';

/**
 * メニュー並び替え store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useMenuSortStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { fetchMenuCategories } = useListStore();
  const [menus, setMenus] = useState<OriginalMenu[]>([]);
  const [sortedMenuId, setSortedMenuId] = useState<OriginalMenu['id'] | null>(
    null,
  );

  const onDragStartMenus = useCallback(
    (menusArg: OriginalMenu[]) => setMenus(menusArg),
    [],
  );

  const onDragEndMenus = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over?.id && active.id !== over?.id) {
        const oldIndex = menus.findIndex(({ id }) => id === active.id);
        const newIndex = menus.findIndex(({ id }) => id === over.id);
        const movedMenus = arrayMove(menus, oldIndex, newIndex).map(
          (menu, idx) => ({
            ...menu,
            displayOrder: idx + 1,
          }),
        );

        dispatch(
          listActions.fetchMenusRelatedToCategorySuccess({
            menus: movedMenus,
          }),
        );

        setSortedMenuId(active.id as number);
        setMenus(movedMenus);
      }
    },
    [menus, dispatch],
  );

  const checkOpenStateSortPopoverMenu = useCallback(
    (id: MenuCategory['id']) => sortedMenuId === id,
    [sortedMenuId],
  );

  const onCancelSortMenu = useCallback(() => {
    setSortedMenuId(null);
    setMenus([]);
    void fetchMenuCategories();
  }, [fetchMenuCategories]);

  const onSortMenu = useCallback(() => {
    void (async () => {
      const [{ categoryId }] = menus;
      const result = await menuCategoryApi.editMenusRelatedToCategory(
        categoryId,
        menus,
      );
      if (result.ok) {
        toast({
          status: 'success',
          description: `メニューの順序を並び替えました。`,
        });
      } else {
        toast({
          status: 'error',
          description: `メニューの並び替えに失敗しました: ${result.err.message}`,
        });
      }
      setSortedMenuId(null);
      void fetchMenuCategories();
    })();
  }, [menus, toast, fetchMenuCategories]);

  return {
    /** メニューの並び替え開始時処理 */
    onDragStartMenus,
    /** メニューの並び替え */
    onDragEndMenus,
    /** 並び替え実行前の確認 popover 表示するかチェックする */
    checkOpenStateSortPopoverMenu,
    /** 並び替え実行キャンセル */
    onCancelSortMenu,
    /** 並び替え実行 */
    onSortMenu,
  };
};
